import api from '../setup/axios';
import config from '../setup/config';

export default {
    /**
     * @method getReport
     * @description - Get the results for an internal report
     * @param { String } reportName - The report name
     * @param { Int } clientId - The client id
     * @param { DateTime } startDateTime - The start date/time
     * @param { DateTime } endDateTime - The end date/time
     * @returns { Array } - The report results
     */
    getReport: async (reportName, clientId, startDateTime, endDateTime, clientCampaignId = null) => {
        const startDateTimeInSeconds = new Date(startDateTime).getTime();
        const endDateTimeInSeconds = new Date(endDateTime).getTime();
        let queryString = `${config.microservices.reports}/?report=${reportName}&startDateTime=${startDateTimeInSeconds}&endDateTime=${endDateTimeInSeconds}&clientId=${clientId}`
        if (clientCampaignId) {
            queryString = queryString += `&clientCampaignId=${clientCampaignId}`
        }
        const response = await api.get(queryString);
        return response.data;
    },
    /**
     * @method getClientList
     * @description - Get the list of Whizeo clients
     * @returns { Promise<Array> } - The list of clients
     */
    getClientList: async () => {
        const response = await api.get(`${config.microservices.clientList}`);
        return response.data;
    },
    /**
     * @method getProductFeatures
     * @description - Get the product features for a given client
     * @param { Int } clientId - The client id
     * @returns { Promise<Array> } - The list of clients
     */
    getProductFeatures: async (clientId) => {
        const response = await api.get(`${config.microservices.supportToolsApi}/client/productFeatures?clientId=${clientId}`);
        return response.data;
    },
    /**
     * @method setProductFeature
     * @description - Set a product feature for a given client
     * @param { Int } clientId - The client id
     * @param { Int } featureId - The feature id
     * @returns { Promise<Bool> } - Id the call was successful
     */
    setProductFeature: async (clientId, featureId, enabled) => {
        const payload = { clientId, featureId, enabled };
        await api.post(`${config.microservices.supportToolsApi}/client/productFeatures`, payload);
    },
    /**
     * @method validateLfClient
     * @description - Get a Lead Forensics client by id
     * @returns { Object } - The LF client details
     */
    validateLfClient: async (lfClientId) => {
        const response = await api.get(`${config.microservices.validateLfClient}?clientId=${lfClientId}`);
        return response.data;
    },
    /**
     * @method createClient
     * @description - Create a Whizeo client
     * @param { String } name - The client name
     * @param { Int } lfClientId - The Lead Forensics client id
     * @param { String } domains - The client's domains
     * @param { String } emailTo - The client's email recipient(s)
     * @param { String } emailCc - The (internal) email CC recipient(s)
     * @param { Int } maxNumberOfSeats - The client's number of seats
     * @param {Boolean} isLfClient - Indicates if client also has LF
     * @returns { Object } - An object with the new clientId value
     */
    createClient: async (name, lfClientId, domains, emailTo, emailCc, maxNumberOfSeats, isLfClient) => {
        const payload = {name, lfClientId, domains, emailTo, emailCc, maxNumberOfSeats, isLfClient};
        const response = await api.post(config.microservices.createClient, payload);
        return response.data;
    },
    /**
     * @method createClient
     * @description - Create a Whizeo client
     * @param { String } name - The client name
     * @param { Int } lfClientId - The Lead Forensics client id
     * @param { String } domains - The client's domains
     * @param { String } emailTo - The client's email recipient(s)
     * @param { String } emailCc - The (internal) email CC recipient(s)
     * @param { Int } maxNumberOfSeats - The client's max number of seats
     * @param {Boolean} isLfClient - Indicates if client also has LF
     * @returns { Object } - An object with the new clientId value
     */
    updateClient: async (clientId, name, domains, emailTo, emailCc, disabled, maxNumberOfSeats, isLfClient) => {
        const payload = {clientId, name, domains, emailTo, emailCc, disabled, maxNumberOfSeats, isLfClient};
        const response = await api.post(`${config.microservices.supportToolsApi}/client`, payload);
        return response.data;
    },
    /**
     * @method updateCodeVersion
     * @description - change code version for a client
     * @param { Int } lfClientId - The Lead Forensics client id
     * @param { Int } codeVersion - Nev code version
     */
    updateCodeVersion: async(clientId, codeVersion) => {
        const payload = {clientId, codeVersion};
        await api.post(`${config.microservices.supportToolsApi}/client/code-version`, payload);
    },
    /**
     * @method createPiUser
     * @description - Create a Whizeo client
     * @param { Int } clientId - The Whizeo client id
     * @param { String } name - The Whizeo client name
     * @returns { Object } - An object with the new PI id value
     */
    createPiUser: (clientId, name) => {
        const payload = {clientId, name};
        api.post(config.microservices.createPiUser, payload);
    },
    /**
     * @method getCampaigns
     * @returns - List of client campaigns
     * @param { Int } clientId - The Whizeo client id
     * @returns { Array } - Array of client campaign objects
     */
    async getClientCampaigns(clientId, includeChatBotCampaigns) {
        const path = `${config.microservices.supportToolsApi}/campaigns?clientId=${clientId}&includeChatBotCampaigns=${includeChatBotCampaigns}`;
        const response = await api.get(path);
        return response.data;
    },
    /**
     * @method getClientWidgets
     * @description - List of client widgets
     * @param { INt } clientId - The Whizeo client id
     * @returns { Array } - Array of client widget objects
     */
    async getClientWidgets(clientId) {
        const path = `${config.microservices.clientWidgetApi}/all?clientId=${clientId}`;
        const response = await api.get(path);
        return response.data;
    },
    /**
     * @method updateSatisfactionLevel
     * @description - Updates a client's satisfaction level
     * @param { Number } clientId
     * @param { Number } satisfactionLevel - A number in [0, 5, 10]
     */
    async saveSatisfactionLevel(clientId, satisfactionLevel) {
        const result = await api.post(`${config.microservices.supportToolsApi}/client/satisfaction-level`, {
            clientId,
            satisfactionLevel
        });
        return result.data;
    }
}
